import { SetupInputConnection, type SetupTotpChallenge } from "@/api/setup-input";
import { SetupInterruptionRecovery } from "./SetupInterruptionRecovery";
import { AppLimitReachedNotice } from "@/features/billing/AppLimitReachedNotice";
import { AppBaseFolderPickerButton } from "./AppBaseFolderPickerButton";
import { storageKindLabel } from "@/lib/storage-kind-label";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { ApiError, apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useConsent,
  useKintoneAdminCredentials,
  useKintoneAppCatalog,
  useProbeKintoneAppFields,
  useSetupJob,
  type EasySetupCredentialSource,
  type KintoneAdminCredentialSummary,
  type SetupJobStepView,
} from "./hooks/useAppBindings";
import { useStorageList } from "@/features/storages/hooks/useStorages";
import { isStorageIntegratedAvailable } from "@/features/storages/storage-ux";
import { useAuth } from "@/hooks/useAuth";
import type { KintoneConnectionAccess } from "@/api/kintone-environment";
import { AppBindingModeSelector } from "./AppBindingModeSelector";
import { CatalogAppCombobox } from "./CatalogAppCombobox";
import {
  defaultAppBaseFolderForMode,
  normalizeRequiredAppBaseFolder,
  type AppBindingModeId,
  type ProbeItem,
  type RecordFolderLabelFieldCandidate,
} from "./app-binding-ux";

const SECURE_SETUP_CONSENT_KIND = "secure_setup_admin_credentials";
// Bound to the recording/retention explanation rendered below.
const SECURE_SETUP_CONSENT_VERSION = 2;

const STEP_LABELS: Record<string, string> = {
  login: "kintone へログイン",
  install_plugin: "プラグインを登録・更新",
  add_plugin: "アプリへプラグインを追加",
  issue_token: "API トークン発行",
  consume_config: "初回連携（プラグイン設定）",
  deploy: "アプリ公開",
};
const STEP_ORDER = [
  "login",
  "install_plugin",
  "add_plugin",
  "issue_token",
  "consume_config",
  "deploy",
];
const INITIAL_PROGRESS_STEPS: SetupJobStepView[] = STEP_ORDER.map((id, index) => ({
  id,
  state: index === 0 ? "running" : "pending",
}));


// User-facing message per terminal job reason. Turns a bare reason code (e.g.
// `invalid_credentials`) into actionable guidance. Unknown reasons fall back to a
// generic message that still shows the raw code.
const SETUP_FAILURE_MESSAGES: Record<string, string> = {
  invalid_credentials:
    "kintone システム管理者のユーザー名またはパスワードが正しくないようです。確認のうえ再入力して、もう一度お試しください。",
  two_factor:
    "二要素認証が有効なアカウントでは簡単セットアップを利用できません。手動セットアップに切り替えてください。",
  saml_only:
    "SAML 認証専用のテナントでは簡単セットアップを利用できません。手動セットアップに切り替えてください。",
  ip_restricted:
    "IP 制限により kintone へ接続できませんでした。許可済みネットワークから実行するか、手動セットアップに切り替えてください。",
  basic_auth_required:
    "Basic 認証が要求される環境では簡単セットアップを利用できません。手動セットアップに切り替えてください。",
  client_cert_required:
    "クライアント証明書が必要な環境では簡単セットアップを利用できません。手動セットアップに切り替えてください。",
  login_failed:
    "kintone へのログインを確認できませんでした。資格情報やネットワークを確認のうえ、もう一度お試しください。",
};

const HOME_KEY = "__home__";
type CredentialMode = "one_time_input" | "saved_shared" | "saved_personal";
type SaveDirective = "none" | "save_shared" | "save_personal";

function savedCredentialRecoveryMessage(
  credentialMode: CredentialMode,
  isCustomerAdmin: boolean,
): string | null {
  if (credentialMode === "saved_shared") {
    return isCustomerAdmin
      ? "保存済みの共有アカウントを利用できません。「アカウントを入力する」を選び、共有アカウントを保存し直してください。"
      : "保存済みの共有アカウントを利用できません。「アカウントを入力する」または自分の個人アカウントを選ぶか、組織管理者に共有アカウントの更新を依頼してください。";
  }
  if (credentialMode === "saved_personal") {
    return "保存済みの個人アカウントを利用できません。「アカウントを入力する」を選び、必要に応じて個人アカウントとして保存し直してください。";
  }
  return null;
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function verifiedCredential(
  credential: KintoneAdminCredentialSummary | null | undefined,
) {
  return credential?.credentialState === "verified" ? credential : null;
}

export interface EasySetupProbeResult {
  guestSpaceId: string | null;
  kintoneApiToken: string;
  customerId: string;
  kintoneSubdomain: string;
  kintoneAppId: string;
  kintoneAppName: string;
  modeId: AppBindingModeId;
  probeItems: ProbeItem[];
  recordFolderLabelFieldCandidates: RecordFolderLabelFieldCandidate[];
}

/**
 * Easy (headless) setup.
 *
 * New setup uses an app picker: the administrator credentials are posted once to
 * list every accessible app grouped by home / regular space / guest space, and
 * the admin searches for an app, optionally filtering by space (no URL parsing, no guest-space-id
 * guesswork — a guest app's space id comes straight from the catalog). Repair
 * re-run (`prefill` + mode="repair") targets a known app and only collects
 * credentials. On a successful job the issued token is handed to the normal
 * settings flow; a failed/unsupported run may offer an explicit manual-setup
 * choice but never switches methods automatically.
 */
export function EasySetupFlow({
  onProbeSuccess,
  onFallbackToManual,
  onBack,
  candidateSubdomain,
  candidateConnectionAccess,
  onRegisterInitialEnvironment,
  prefill,
  mode = "new",
}: {
  onProbeSuccess: (result: EasySetupProbeResult) => void;
  onFallbackToManual: () => void;
  onBack: () => void;
  candidateSubdomain?: string;
  candidateConnectionAccess?: KintoneConnectionAccess;
  onRegisterInitialEnvironment?: (input: {
    kintoneAppId: string;
    administratorLoginName: string;
    administratorPassword: string;
    administratorMfa?: { kind: "disabled" } | { kind: "totp_key"; setupKey: string } | { kind: "totp_runtime" };
    inputConnection?: SetupInputConnection;
  }) => Promise<void>;
  /** Repair re-run: target a known app so only credentials are entered. */
  prefill?: {
    kintoneAppId: string;
    guestSpaceId?: string | null;
    modeId: AppBindingModeId;
  };
  mode?: "new" | "repair";
}) {
  const { user } = useAuth();
  const isCustomerAdmin = user?.currentMembership?.role === "customer_admin";
  const { status: consent, agree } = useConsent(SECURE_SETUP_CONSENT_KIND);
  const { job, issuedToken, error: jobError, errorCode: jobErrorCode, start, cancel, refresh } = useSetupJob();
  // C6: the plan's app limit refused the new app; show the plan guidance instead of the raw error.
  const appLimitReached = jobErrorCode === "app_limit_reached";
  const { mutate: probeFields, error: probeError } = useProbeKintoneAppFields();
  const {
    catalog,
    fetchCatalog,
    isLoading: catalogLoading,
    error: catalogError,
    reset: resetCatalog,
  } = useKintoneAppCatalog();

  const isRepair = mode === "repair" && Boolean(prefill);
  const registeredKintoneSubdomain = user?.serviceSubscription?.kintoneSubdomain ?? "";
  const isInitialCandidate = Boolean(candidateSubdomain && !registeredKintoneSubdomain);
  const catalogKintoneSubdomain =
    registeredKintoneSubdomain || candidateSubdomain || "";
  const {
    credentials: savedCredentials,
    isLoading: savedCredentialsLoading,
    error: savedCredentialsError,
    refresh: refreshSavedCredentials,
  } = useKintoneAdminCredentials({ enabled: !isInitialCandidate });
  const {
    items: storages,
    isLoading: storagesLoading,
    error: storagesError,
  } = useStorageList({ limit: "100" });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaMode, setMfaMode] = useState<"disabled" | "totp_key" | "totp_runtime">("disabled");
  const [mfaSetupKey, setMfaSetupKey] = useState("");
  const [inputConnectionAttempt, setInputConnectionAttempt] = useState(0);
  const [inputConnection, setInputConnection] = useState<SetupInputConnection>();
  const [totpChallenge, setTotpChallenge] = useState<SetupTotpChallenge | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpInputError, setTotpInputError] = useState<string | null>(null);
  const [credentialMode, setCredentialMode] =
    useState<CredentialMode>("one_time_input");
  const [credentialModeTouched, setCredentialModeTouched] = useState(false);
  const [saveDirective, setSaveDirective] = useState<SaveDirective>("save_personal");
  const [overwriteConfirmed, setOverwriteConfirmed] = useState(false);
  const [modeId, setModeId] = useState<AppBindingModeId | "">(prefill?.modeId ?? "");
  const [spaceKey, setSpaceKey] = useState("");
  const [appKey, setAppKey] = useState("");
  const [storageId, setStorageId] = useState("");
  const [appBaseFolder, setAppBaseFolder] = useState("");
  const [appBaseFolderTouched, setAppBaseFolderTouched] = useState(false);
  const [connectedSavedCredential, setConnectedSavedCredential] =
    useState<KintoneAdminCredentialSummary | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [initialRegistrationError, setInitialRegistrationError] =
    useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [probing, setProbing] = useState(false);
  const beginJobPendingRef = useRef(false);
  const consentPending = useRef(false);
  // Probe at most once per issued token (selected is a fresh object each render).
  const probedTokenRef = useRef<string | null>(null);

  const consented = consentAgreed || Boolean(consent?.consented && consent.agreedVersion !== null && consent.agreedVersion >= SECURE_SETUP_CONSENT_VERSION);
  const credentialRejected = catalogError?.code === "kintone_credentials_rejected";
  const credentialInputRejected =
    credentialRejected && credentialMode === "one_time_input";
  const credentialErrorId = credentialInputRejected
    ? "easyCredential-authentication-error"
    : undefined;
  const rejectedSavedCredentialRecovery =
    credentialRejected
      ? savedCredentialRecoveryMessage(credentialMode, isCustomerAdmin)
      : null;
  const currentConnectedSavedCredential =
    connectedSavedCredential?.kintoneSubdomain === catalogKintoneSubdomain
      ? connectedSavedCredential
      : null;
  const sharedSavedCredential =
    currentConnectedSavedCredential?.credentialScopeKind === "shared"
      ? currentConnectedSavedCredential
      : savedCredentials?.shared;
  const personalSavedCredential =
    currentConnectedSavedCredential?.credentialScopeKind === "personal"
      ? currentConnectedSavedCredential
      : savedCredentials?.personal;
  const selectedSavedCredential =
    credentialMode === "saved_shared"
      ? verifiedCredential(sharedSavedCredential)
      : credentialMode === "saved_personal"
        ? verifiedCredential(personalSavedCredential)
        : null;
  const runtimeTotp = credentialMode === "one_time_input" ? mfaMode === "totp_runtime" : selectedSavedCredential?.mfaMode === "totp_runtime";
  useEffect(() => {
    if (!runtimeTotp) { setInputConnection(undefined); return; }
    let disposed = false;
    const connection = new SetupInputConnection(
      prompt => { if (!disposed) { setTotpChallenge(prompt); setTotpCode(""); setTotpInputError(null); } },
      message => { if (!disposed) { setTotpInputError(message); setTotpCode(""); setInputConnection(undefined); } },
    );
    setInputConnection(connection);
    return () => { disposed = true; connection.close(); setTotpChallenge(null); setTotpCode(""); };
  }, [runtimeTotp, inputConnectionAttempt]);
  const saveTargetCredential =
    saveDirective === "save_shared"
      ? sharedSavedCredential
      : saveDirective === "save_personal"
        ? personalSavedCredential
        : null;
  const overwriteRequired = Boolean(saveTargetCredential);
  const credsFilled = (!runtimeTotp || Boolean(inputConnection)) && (
    credentialMode === "one_time_input"
      ? username.trim().length > 0 &&
        password.length > 0 &&
        (mfaMode !== "totp_key" || mfaSetupKey.trim().length > 0) &&
        !(mfaMode === "totp_runtime" && saveDirective === "save_shared") &&
        (!overwriteRequired || overwriteConfirmed)
      : Boolean(selectedSavedCredential));
  const readyStorages = useMemo(
    () => storages.filter(isStorageIntegratedAvailable),
    [storages],
  );
  const selectedStorage =
    readyStorages.find((storage) => storage.storageId === storageId) ?? null;

  // Keep the owning space with each candidate, including when all spaces are shown.
  const spaceApps = useMemo(() => {
    if (!catalog) return [];
    const allApps = [
      ...catalog.home.map((app) => ({
        ...app, value: `${HOME_KEY}:${app.appId}`, spaceKey: HOME_KEY,
        spaceLabel: "ホーム（スペースなし）", guestSpaceId: null,
      })),
      ...catalog.spaces.flatMap((space) => space.apps.map((app) => ({
        ...app, value: `${space.spaceId}:${app.appId}`, spaceKey: space.spaceId,
        spaceLabel: space.kind === "guest"
          ? `${space.name ?? `ゲストスペース ${space.spaceId}`}（ゲスト）`
          : (space.name ?? `スペース ${space.spaceId}`),
        guestSpaceId: space.kind === "guest" ? space.spaceId : null,
      }))),
    ];
    return spaceKey ? allApps.filter((app) => app.spaceKey === spaceKey) : allApps;
  }, [catalog, spaceKey]);

  // Unified selected app identity for both the picker and repair prefill.
  const selected = useMemo(() => {
    if (isRepair && prefill) {
      return {
        appId: prefill.kintoneAppId,
        guestSpaceId: prefill.guestSpaceId ?? null,
        appName: null,
      };
    }
    const app = spaceApps.find((candidate) => candidate.value === appKey);
    if (!app || app.bound) return null;
    return {
      appId: app.appId,
      appName: app.name,
      guestSpaceId: app.guestSpaceId,
    };
  }, [isRepair, prefill, spaceApps, appKey]);
  const customerId = user?.serviceSubscription?.customerId ?? "";

  const defaultAppBaseFolder =
    selected && modeId
      ? defaultAppBaseFolderForMode({
          modeId,
          kintoneAppId: selected.appId,
        })
      : "/";
  const normalizedAppBaseFolder = normalizeRequiredAppBaseFolder(appBaseFolder);
  const trimmedAppBaseFolder = appBaseFolder.trim();
  const appBaseFolderValidationMessage = !appBaseFolderTouched
    ? null
    : !trimmedAppBaseFolder
      ? "アプリベースフォルダを入力してください。"
      : !trimmedAppBaseFolder.startsWith("/")
        ? "先頭に「/」を付けて入力してください（例: /team/documents）。"
        : !normalizedAppBaseFolder
          ? "「/」から始まる有効なパスを入力してください。「\\」や「//」、「.」「..」は使用できません。"
          : null;
  const newBindingSettingsReady =
    isRepair ||
    (Boolean(selected?.appName) &&
      Boolean(selectedStorage) &&
      Boolean(normalizedAppBaseFolder));
  const canStart =
    Boolean(selected) && Boolean(modeId) && credsFilled && newBindingSettingsReady;

  useEffect(() => {
    if (isInitialCandidate || credentialModeTouched || !savedCredentials) return;
    if (isCustomerAdmin && savedCredentials.shared?.credentialState === "verified") {
      setCredentialMode("saved_shared");
    } else if (savedCredentials.personal?.credentialState === "verified") {
      setCredentialMode("saved_personal");
    } else if (savedCredentials.shared?.credentialState === "verified") {
      setCredentialMode("saved_shared");
    }
  }, [credentialModeTouched, isCustomerAdmin, isInitialCandidate, savedCredentials]);

  useEffect(() => {
    if (isCustomerAdmin) return;
    if (saveDirective === "save_shared") {
      setSaveDirective("save_personal");
      setOverwriteConfirmed(false);
    }
  }, [isCustomerAdmin, saveDirective]);

  useEffect(() => {
    if (isRepair || appBaseFolderTouched || !selected || !modeId) return;
    setAppBaseFolder(defaultAppBaseFolder);
  }, [appBaseFolderTouched, defaultAppBaseFolder, isRepair, modeId, selected]);

  function selectCredentialMode(next: CredentialMode) {
    setCredentialMode(next);
    setCredentialModeTouched(true);
    if (catalogError) resetCatalog();
  }

  function buildCredentialSource(): EasySetupCredentialSource {
    if (isInitialCandidate) {
      return {
        kind: "one_time_input",
        username: username.trim(),
        password,
        mfa: mfaMode === "totp_key" ? { kind: "totp_key", setupKey: mfaSetupKey } : { kind: mfaMode },
        saveDirective: "none",
      };
    }
    if (credentialMode === "saved_shared" && selectedSavedCredential) {
      return {
        kind: "saved_shared",
        expectedCredentialRevision: selectedSavedCredential.credentialRevision,
      };
    }
    if (credentialMode === "saved_personal" && selectedSavedCredential) {
      return {
        kind: "saved_personal",
        expectedCredentialRevision: selectedSavedCredential.credentialRevision,
      };
    }
    return {
      kind: "one_time_input",
      username: username.trim(),
      password,
      mfa: mfaMode === "totp_key" ? { kind: "totp_key", setupKey: mfaSetupKey } : { kind: mfaMode },
      saveDirective,
      expectedCredentialRevision: saveTargetCredential?.credentialRevision,
      overwriteConfirmed,
    };
  }

  async function withConsent(action: () => void) {
    if (consentPending.current) return;
    consentPending.current = true;
    setConsentError(null);
    try {
      if (!consented) {
        const accepted = await agree(SECURE_SETUP_CONSENT_VERSION);
        if (!accepted.consented || accepted.requiredVersion !== SECURE_SETUP_CONSENT_VERSION || accepted.agreedVersion !== SECURE_SETUP_CONSENT_VERSION) {
          throw new Error('consent_version_mismatch');
        }
        setConsentAgreed(true);
      }
      action();
    } catch {
      setConsentError("操作の許可を記録できませんでした。ページを再読み込みして、もう一度お試しください。");
    } finally {
      consentPending.current = false;
    }
  }

  function handleFetchCatalog() {
    if (!credsFilled || !catalogKintoneSubdomain) return;
    if (isInitialCandidate && !candidateConnectionAccess) return;
    withConsent(() => {
      void (async () => {
        const requestedCredentialSource = buildCredentialSource();
        try {
          const result = await fetchCatalog({
            targetSubdomain: catalogKintoneSubdomain,
            ...(isInitialCandidate
              ? { connectionAccess: candidateConnectionAccess! }
              : {}),
            credentialSource: requestedCredentialSource,
            ...(runtimeTotp ? { inputConnection } : {}),
          });
          if (result?.savedCredential) {
            setConnectedSavedCredential(result.savedCredential);
            setCredentialMode(
              result.savedCredential.credentialScopeKind === "shared"
                ? "saved_shared"
                : "saved_personal",
            );
            setCredentialModeTouched(true);
            setUsername("");
            setPassword("");
      setMfaSetupKey("");
            setSaveDirective("save_personal");
            setOverwriteConfirmed(false);
          } else if (requestedCredentialSource.kind === "one_time_input" &&
                     requestedCredentialSource.saveDirective !== "none") {
            // A lost response can leave the commit outcome unknown. Do not keep
            // plaintext or infer success; re-read the non-secret server summary.
            setUsername("");
            setPassword("");
      setMfaSetupKey("");
            await refreshSavedCredentials({ force: true });
          }
        } catch {
          // hooks expose the user-facing error beside the form.
        }
      })();
    });
  }

  async function beginJob() {
    if (beginJobPendingRef.current) return;
    if (!selected || !modeId) return;
    if (mode === "new" && !normalizedAppBaseFolder) return;
    beginJobPendingRef.current = true;
    setStarted(true);
    setInitialRegistrationError(null);
    try {
      let jobCredentialSource = buildCredentialSource();
      if (isInitialCandidate) {
        if (!onRegisterInitialEnvironment ||
            credentialMode !== "one_time_input" ||
            !username.trim() ||
            !password) {
          throw new Error("初回kintone環境の確認情報が不足しています。");
        }
        await onRegisterInitialEnvironment({
          kintoneAppId: selected.appId,
          administratorLoginName: username.trim(),
          administratorPassword: password,
          ...(runtimeTotp ? { inputConnection } : {}),
          administratorMfa: mfaMode === "totp_key" ? { kind: "totp_key", setupKey: mfaSetupKey } : { kind: mfaMode },
        });
        if (saveDirective !== "none") {
          const savedCatalog = await fetchCatalog({
            ...(runtimeTotp ? { inputConnection } : {}),
            targetSubdomain: catalogKintoneSubdomain,
            credentialSource: {
              kind: "one_time_input",
              username: username.trim(),
              password,
              mfa: mfaMode === "totp_key" ? { kind: "totp_key", setupKey: mfaSetupKey } : { kind: mfaMode },
              saveDirective,
            },
          });
          setUsername("");
          setPassword("");
          setMfaSetupKey("");
          if (!savedCatalog?.savedCredential) {
            await refreshSavedCredentials({ force: true });
            throw new Error(
              "管理者アカウントの保存結果を確認できませんでした。保存済みアカウントを確認してから再試行してください。",
            );
          }
          const savedCredential = savedCatalog.savedCredential;
          setConnectedSavedCredential(savedCredential);
          setCredentialMode(
            savedCredential.credentialScopeKind === "shared"
              ? "saved_shared"
              : "saved_personal",
          );
          setCredentialModeTouched(true);
          setSaveDirective("save_personal");
          jobCredentialSource = {
            kind: savedCredential.credentialScopeKind === "shared"
              ? "saved_shared"
              : "saved_personal",
            expectedCredentialRevision: savedCredential.credentialRevision,
          };
        }
      }
      const newBindingInput =
        mode === "new" && selected.appName && normalizedAppBaseFolder
          ? {
              storageId,
              appBaseFolder: normalizedAppBaseFolder,
              kintoneAppName: selected.appName,
            }
          : {};
      await start({
        kintoneAppId: selected.appId,
        guestSpaceId: selected.guestSpaceId,
        credentialSource: jobCredentialSource,
        ...(runtimeTotp ? { inputConnection } : {}),
        modeId,
        mode,
        ...newBindingInput,
      });
      // Credentials are no longer needed locally once the job has them.
      setPassword("");
      setMfaSetupKey("");
    } catch (cause) {
      beginJobPendingRef.current = false;
      // Job creation failed (e.g. binding_already_activated); return to the form.
      // The error is surfaced via jobError.
      setStarted(false);
      if (isInitialCandidate && !(cause instanceof ApiError && cause.code === "app_limit_reached")) {
        setInitialRegistrationError(
          apiErrorMessage(
            cause,
            "kintone環境を登録してセットアップを開始できませんでした。入力内容を確認して、もう一度お試しください。",
          ),
        );
      }
    }
  }

  function handleStart() {
    if (!canStart) return;
    withConsent(() => void beginJob());
  }

  // When a token is issued, probe with it and hand off to the settings flow.
  useEffect(() => {
    if (
      !issuedToken ||
      probedTokenRef.current === issuedToken ||
      !selected ||
      !modeId
    )
      return;
    probedTokenRef.current = issuedToken;
    setProbing(true);
    const s = selected;
    const chosenMode = modeId;
    void (async () => {
      try {
        const result = await probeFields({
          kintoneAppId: s.appId,
          guestSpaceId: s.guestSpaceId,
          kintoneApiToken: issuedToken,
        });
        onProbeSuccess({
          guestSpaceId: s.guestSpaceId,
          kintoneApiToken: issuedToken,
          customerId,
          kintoneSubdomain: result.kintoneSubdomain,
          kintoneAppId: result.kintoneAppId,
          kintoneAppName: result.kintoneAppName,
          modeId: chosenMode,
          probeItems: result.items,
          recordFolderLabelFieldCandidates: result.recordFolderLabelFieldCandidates,
        });
      } catch {
        setProbing(false);
      }
    })();
  }, [issuedToken, selected, modeId, customerId, probeFields, onProbeSuccess]);

  useEffect(() => { if (job && ["queued", "running", "cancel_requested", "reconciliation_required"].includes(job.status)) setStarted(true); }, [job]);

  const terminalFallback =
    job &&
    (job.status === "fallback_manual" || job.status === "failed") &&
    !issuedToken;
  const progressSteps = useMemo(() => {
    if (!started) return [];
    const serverSteps = job?.steps ?? [];
    if (serverSteps.length === 0) return INITIAL_PROGRESS_STEPS;
    const serverStepById = new Map(serverSteps.map((step) => [step.id, step]));
    const orderedSteps = STEP_ORDER.map((id) =>
      serverStepById.get(id) ?? INITIAL_PROGRESS_STEPS.find((step) => step.id === id)!,
    );
    const extraSteps = serverSteps.filter((step) => !STEP_ORDER.includes(step.id));
    return [...orderedSteps, ...extraSteps];
  }, [job?.steps, started]);

  function renderCredentialSummary(credential: KintoneAdminCredentialSummary | null) {
    if (!credential) return null;
    return (
      <span className="text-xs text-muted-foreground">
        {credential.kintoneAdminAccountName} /{" "}
        {credential.credentialState === "verified" ? "検証済み" : "再検証が必要"}
        {credential.mfaMode === "totp_key" ? " / MFAキー保存済み" : credential.mfaMode === "totp_runtime" ? " / 実行時MFAコード入力" : " / MFAなし"}
        {credential.lastUsedAt ? ` / 最終利用 ${formatDateTime(credential.lastUsedAt)}` : ""}
      </span>
    );
  }

  const hasCredentialChoice = !isInitialCandidate && Boolean(sharedSavedCredential || personalSavedCredential);
  const credentialFields = (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        自動操作の画面・入力内容（パスワードや認証コードを含む）を、失敗調査のため暗号化して記録します。
        記録は成功時に削除し、失敗・中断時は操作開始から7日間保存します。
      </p>
      {consentError ? <p role="alert" className="text-xs text-destructive">{consentError}</p> : null}

      {!isInitialCandidate && savedCredentialsLoading ? (
        <p className="text-xs text-muted-foreground">保存済みアカウントを確認しています…</p>
      ) : null}
      {!isInitialCandidate && savedCredentialsError ? (
        <p className="text-xs text-destructive" role="alert">
          {savedCredentialsError}
        </p>
      ) : null}

      {!isInitialCandidate && sharedSavedCredential ? (
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="easyCredentialMode"
            value="saved_shared"
            checked={credentialMode === "saved_shared"}
            onChange={() => selectCredentialMode("saved_shared")}
            disabled={sharedSavedCredential.credentialState !== "verified"}
            className="mt-1"
          />
          <span className="space-y-0.5">
            <span className="block font-medium">共有アカウントを使う</span>
            {renderCredentialSummary(sharedSavedCredential)}
          </span>
        </label>
      ) : null}

      {!isInitialCandidate && personalSavedCredential ? (
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="easyCredentialMode"
            value="saved_personal"
            checked={credentialMode === "saved_personal"}
            onChange={() => selectCredentialMode("saved_personal")}
            disabled={personalSavedCredential.credentialState !== "verified"}
            className="mt-1"
          />
          <span className="space-y-0.5">
            <span className="block font-medium">個人アカウントを使う</span>
            {renderCredentialSummary(personalSavedCredential)}
          </span>
        </label>
      ) : null}

      {hasCredentialChoice ? <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          name="easyCredentialMode"
          value="one_time_input"
          checked={credentialMode === "one_time_input"}
          onChange={() => selectCredentialMode("one_time_input")}
          className="mt-1"
        />
        <span className="font-medium">アカウントを入力する</span>
      </label> : null}

      {credentialMode === "one_time_input" ? (
        <div className={hasCredentialChoice ? "space-y-3 pl-6" : "space-y-3"}>
          <div className="space-y-2">
            <Label htmlFor="easyUser">ユーザー名</Label>
            <Input
              id="easyUser"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (catalogError) resetCatalog();
              }}
              autoComplete="off"
              aria-invalid={credentialInputRejected || undefined}
              aria-describedby={credentialErrorId}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="easyPass">パスワード</Label>
            <Input
              id="easyPass"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (catalogError) resetCatalog();
              }}
              autoComplete="off"
              aria-invalid={credentialInputRejected || undefined}
              aria-describedby={credentialErrorId}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="easyMfaMode">多要素認証</Label>
            <select id="easyMfaMode" value={mfaMode}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              onChange={(e) => {
                setMfaMode(e.target.value as "disabled" | "totp_key" | "totp_runtime");
                setMfaSetupKey("");
                if (catalogError) resetCatalog();
              }}>
              <option value="disabled">設定していない</option>
              <option value="totp_key">認証アプリのセットアップキーを使用</option>
              <option value="totp_runtime" disabled={saveDirective === "save_shared"}>実行時に認証コードを入力</option>
            </select>
            {mfaMode === "totp_runtime" ? <p className="text-xs text-muted-foreground">
              {saveDirective === "save_shared" ? "共有保存ではセットアップキーが必要です。多要素認証の方式を変更してください。" : "認証コードは要求されたときに入力します。"}
            </p> : null}
            {mfaMode === "totp_key" ? <>
              <Label htmlFor="easyMfaKey">認証アプリのセットアップキー</Label>
              <Input id="easyMfaKey" type="password" autoComplete="off"
                value={mfaSetupKey} onChange={(e) => {
                  setMfaSetupKey(e.target.value);
                  if (catalogError) resetCatalog();
                }} />
              <p className="text-xs text-muted-foreground">
                {saveDirective === "none"
                  ? "今回の認証に使用します。次回の設定用には保存しません。"
                  : saveDirective === "save_shared"
                    ? "キーを暗号化して共有保存します。この組織で共有アカウントを利用できる人は、多要素認証を含めて設定を実行できます。"
                    : "キーを暗号化して個人保存します。キー自体は再表示しません。"}
              </p>
            </> : null}
          </div>
          {credentialInputRejected ? (
            <p
              id="easyCredential-authentication-error"
              className="text-xs text-destructive"
              role="alert"
            >
              {catalogError.message}
            </p>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="easyCredentialSave">アカウントの保存</Label>
            <select
              id="easyCredentialSave"
              aria-describedby="easyCredentialSave-description"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={saveDirective}
              onChange={(e) => {
                setSaveDirective(e.target.value as SaveDirective);
                setOverwriteConfirmed(false);
              }}
            >
              <option value="none">次回用に保存しない（今回だけ使用）</option>
              {isCustomerAdmin ? (
                <option value="save_shared">共有アカウントとして保存</option>
              ) : null}
              <option value="save_personal">個人アカウントとして保存</option>
            </select>
            <p id="easyCredentialSave-description" className="text-xs text-muted-foreground">
              {saveDirective === "save_personal"
                ? "今後あなたが設定する際に利用できるよう、アカウント情報を暗号化して保存します。"
                : saveDirective === "save_shared"
                  ? "今後この組織の利用者が設定する際に利用できるよう、アカウント情報を暗号化して保存します。"
                  : "アカウント情報は今回の設定に使用し、次回用には保存しません。失敗調査用の操作記録は上記の条件で保持します。"}
            </p>
          </div>
          {!isInitialCandidate && overwriteRequired && saveTargetCredential ? (
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={overwriteConfirmed}
                onChange={(e) => setOverwriteConfirmed(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                既存の
                {saveDirective === "save_shared" ? "共有" : "個人"}
                アカウント（{saveTargetCredential.kintoneAdminAccountName}）を上書きする
              </span>
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const modeField = (
    <AppBindingModeSelector
      value={modeId}
      onChange={(mode) => {
        setModeId(mode);
        setAppBaseFolderTouched(false);
      }}
    />
  );

  const storageAndFolderFields = !isRepair ? (
    <div className="space-y-4 rounded-md border p-3">
      <div className="space-y-2">
        <Label htmlFor="easyStorage">保存先ストレージ</Label>
        <select
          id="easyStorage"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={storageId}
          aria-describedby={storageId && (!selected || !modeId) ? "easyStorage-selection-warning" : undefined}
          onChange={(event) => setStorageId(event.target.value)}
          disabled={storagesLoading || readyStorages.length === 0}
        >
          <option value="">選択してください</option>
          {readyStorages.map((storage) => (
            <option key={storage.storageId} value={storage.storageId}>
              {storage.displayName} / {storageKindLabel(storage.connectionKindId, storage.providerFamilyDisplayLabel)}
            </option>
          ))}
        </select>
        {storageId && (!selected || !modeId) ? (
          <p id="easyStorage-selection-warning" className="text-sm text-destructive" role="alert">
            {!selected && !modeId
              ? "アプリと利用モードを選択してください。"
              : !selected
                ? "アプリを選択してください。"
                : "利用モードを選択してください。"}
          </p>
        ) : null}
        {storagesLoading ? (
          <p className="text-xs text-muted-foreground">ストレージを確認しています…</p>
        ) : null}
        {storagesError ? (
          <p className="text-xs text-destructive" role="alert">
            {storagesError}
          </p>
        ) : null}
        {!storagesLoading && readyStorages.length === 0 ? (
          <p className="text-xs text-destructive" role="alert">
            利用可能なストレージがありません。先にストレージを利用可能にしてください。
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="easyAppBaseFolder">アプリベースフォルダ</Label>
        <Input
          id="easyAppBaseFolder"
          value={appBaseFolder}
          readOnly
          placeholder={defaultAppBaseFolder}
          aria-invalid={Boolean(appBaseFolderValidationMessage)}
          aria-describedby={
            appBaseFolderValidationMessage
              ? "easyAppBaseFolder-help easyAppBaseFolder-error"
              : "easyAppBaseFolder-help"
          }
        />
        <p id="easyAppBaseFolder-help" className="text-xs text-muted-foreground">
          フォルダ選択画面で既存フォルダまたは新しい保存先パスを指定できます。ストレージ直下は「/」です。
        </p>
        <AppBaseFolderPickerButton
          storageId={storageId}
          suggestedPath={appBaseFolder || defaultAppBaseFolder}
          onSelect={(path) => {
            setAppBaseFolder(path);
            setAppBaseFolderTouched(true);
          }}
        />
        {appBaseFolderValidationMessage ? (
          <p id="easyAppBaseFolder-error" className="text-xs text-destructive" role="alert">
            {appBaseFolderValidationMessage}
          </p>
        ) : null}
      </div>
    </div>
  ) : null;

  function returnToCredentialSelection() {
    setStarted(false);
    if (isRepair) return;
    resetCatalog();
    setSpaceKey("");
    setAppKey("");
    setStorageId("");
    setAppBaseFolder("");
    setAppBaseFolderTouched(false);
  }

  function handleTerminalRecovery() {
    if (job?.reason === "invalid_credentials") {
      returnToCredentialSelection();
      return;
    }
    setStarted(false);
  }

  function renderNewFlow() {
    if (!catalog) {
      return (
        <div className="max-w-lg space-y-4">
          <p className="text-sm text-muted-foreground">
            kintone管理者アカウントを入力すると、プラグインのインストールやAPIトークンの発行、設定の反映を自動で行えます。
          </p>
          {catalogKintoneSubdomain ? (
            <div className="text-sm">
              <span className="text-muted-foreground">接続先: </span>
              <span className="font-mono">{catalogKintoneSubdomain}</span>
            </div>
          ) : null}
          {credentialFields}
          {!catalogKintoneSubdomain ? (
            <p className="text-sm text-destructive" role="alert">
              有効なサービス利用と固定 kintone サブドメインを確認できません。
            </p>
          ) : null}
          {catalogError && !credentialInputRejected ? (
            <p className="text-sm text-destructive" role="alert">
              {catalogError.message}
              {rejectedSavedCredentialRecovery ? (
                <span className="mt-1 block">{rejectedSavedCredentialRecovery}</span>
              ) : null}
            </p>
          ) : null}
          <Button
            type="button"
            onClick={handleFetchCatalog}
            disabled={
              !credsFilled ||
              !catalogKintoneSubdomain ||
              catalogLoading
            }
          >
            {catalogLoading ? "kintone に接続中…" : "kintone に接続"}
          </Button>
          <p className="text-sm text-muted-foreground">
            アカウントを入力せずに設定する場合は、
            <button type="button" className="text-primary underline underline-offset-4" onClick={onFallbackToManual}>手動セットアップへ</button>
          </p>
        </div>
      );
    }

    const catalogHeader = (
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>サブドメイン: {catalog.kintoneSubdomain}</span>
        <button
          type="button"
          className="underline hover:text-foreground"
          onClick={() => {
            resetCatalog();
            setSpaceKey("");
            setAppKey("");
            setStorageId("");
            setAppBaseFolder("");
            setAppBaseFolderTouched(false);
          }}
        >
          別のアカウントで取得し直す
        </button>
      </div>
    );

    if (catalog.home.length === 0 && catalog.spaces.length === 0) {
      return (
        <div className="max-w-lg space-y-4">
          {catalogHeader}
          <p className="text-sm text-muted-foreground" role="alert">
            このアカウントで連携可能な kintoneアプリが見つかりませんでした。
            kintone システム管理権限があり、対象アプリを管理できるアカウントで取得し直してください。
          </p>
        </div>
      );
    }

    return (
      <div className="max-w-5xl space-y-6">
        {catalogHeader}

        <div className="space-y-2">
          <Label htmlFor="easySpace">スペース</Label>
          <select
            id="easySpace"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={spaceKey}
            onChange={(e) => {
              setSpaceKey(e.target.value);
              setAppKey("");
              setAppBaseFolderTouched(false);
            }}
          >
            <option value="">すべてのスペース（ホームを含む）</option>
            {catalog.home.length > 0 ? (
              <option value={HOME_KEY}>ホーム（スペースなし）</option>
            ) : null}
            {catalog.spaces.map((s) => (
              <option key={s.spaceId} value={s.spaceId}>
                {s.kind === "guest"
                  ? `${s.name ?? `ゲストスペース ${s.spaceId}`}（ゲスト）`
                  : (s.name ?? `スペース ${s.spaceId}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="easyApp">アプリ</Label>
          <CatalogAppCombobox
            key={spaceKey}
            id="easyApp"
            options={spaceApps}
            value={appKey}
            onChange={(value) => {
              setAppKey(value);
              setAppBaseFolderTouched(false);
            }}
          />
          <p id="easyApp-help" className="text-xs text-muted-foreground">
            アプリ名・アプリIDで絞り込めます。スペースを選ぶと、そのスペースのアプリだけを表示します。
          </p>
          {spaceKey && spaceApps.length > 0 && spaceApps.every((a) => a.bound) ? (
            <p className="text-xs text-muted-foreground">
              このスペースのアプリはすべて連携済みです。
            </p>
          ) : null}
        </div>

        {modeField}
        {storageAndFolderFields}

        {initialRegistrationError ? (
          <p className="text-sm text-destructive" role="alert">
            {initialRegistrationError}
          </p>
        ) : null}
        {appLimitReached ? (
          <AppLimitReachedNotice isAdmin={isCustomerAdmin} />
        ) : jobError ? (
          <p className="text-sm text-destructive" role="alert">
            {jobError}
          </p>
        ) : null}

        <p className="text-xs text-muted-foreground">
          開始すると、選択したアカウントでプラグインの導入・設定、APIトークンの発行、アプリの公開を行います。
        </p>
        <Button type="button" onClick={handleStart} disabled={!canStart}>
          セットアップを開始
        </Button>
      </div>
    );
  }

  function renderRepairForm() {
    return (
      <div className="max-w-lg space-y-4">
        <p className="text-sm text-muted-foreground">
          既存の連携を修復します。プラグインの追加・公開、API トークンは冪等に再利用されます。
          自動セットアップで使用するkintone管理者アカウントを入力してください。
        </p>
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <span className="text-muted-foreground">対象アプリ: </span>
          {catalogKintoneSubdomain} / アプリID {prefill?.kintoneAppId}
          {prefill?.guestSpaceId ? `（ゲストスペース ${prefill.guestSpaceId}）` : ""}
        </div>
        {credentialFields}
        {jobError ? (
          <p className="text-sm text-destructive" role="alert">
            {jobError}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          開始すると、選択したアカウントでプラグインの導入・設定、APIトークンの発行、アプリの公開を行います。
        </p>
        <Button type="button" onClick={handleStart} disabled={!canStart}>
          セットアップを開始
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">簡単セットアップ</h1>
      </div>

      {totpChallenge ? <section aria-label="多要素認証コードの入力" className="mb-6 space-y-2">
        <p>認証アプリの現在のコードを入力してください（試行 {totpChallenge.attempt}/3）。期限 {formatDateTime(totpChallenge.expiresAt)}</p>
        <Label htmlFor="easyTotpCode">認証コード</Label>
        <Input id="easyTotpCode" type="password" inputMode="numeric" autoComplete="off" maxLength={6}
          value={totpCode} onChange={e => setTotpCode(e.target.value)} />
        <Button disabled={!/^[0-9]{6}$/.test(totpCode)} onClick={() => {
          const code = totpCode; setTotpCode("");
          void inputConnection?.submit(code).catch(() => setTotpInputError("認証コードを送信できませんでした。入力要求と期限を確認してください。"));
        }}>認証コードを送信</Button>
        <Button variant="outline" onClick={() => { inputConnection?.close(); setTotpCode(""); }}>認証入力を取り消す</Button>
      </section> : null}
      {totpInputError ? <div><p role="alert" className="text-destructive">{totpInputError}</p>
        {runtimeTotp && !inputConnection ? <Button variant="outline" onClick={() => { setTotpInputError(null); setInputConnectionAttempt(value => value + 1); }}>認証入力の接続を開き直す</Button> : null}
      </div> : null}

      {!started ? (
        isRepair ? (
          renderRepairForm()
        ) : (
          renderNewFlow()
        )
      ) : (
        <div className="max-w-lg space-y-4">
          <ol className="space-y-2">
            {progressSteps.map((step) => (
              <li key={step.id} className="flex items-center gap-2 text-sm">
                <span
                  className={[
                    "inline-flex h-5 w-5 items-center justify-center rounded-full text-xs",
                    step.state === "done"
                      ? "bg-green-100 text-green-800"
                      : step.state === "failed"
                        ? "bg-red-100 text-red-800"
                        : step.state === "running"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-500",
                  ].join(" ")}
                >
                  {step.state === "done"
                    ? "✓"
                    : step.state === "failed"
                      ? "✕"
                      : step.state === "running"
                        ? "…"
                        : "·"}
                </span>
                <span>{STEP_LABELS[step.id] ?? step.id}</span>
              </li>
            ))}
          </ol>

          {probing ? (
            <p className="text-sm text-muted-foreground">設定画面を準備しています…</p>
          ) : null}

          {terminalFallback ? (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p>
                {(job?.reason === "invalid_credentials"
                  ? savedCredentialRecoveryMessage(credentialMode, isCustomerAdmin)
                  : null) ??
                  (job?.reason && SETUP_FAILURE_MESSAGES[job.reason]) ??
                  `自動セットアップを完了できませんでした${
                    job?.reason ? `（${job.reason}）` : ""
                  }。もう一度実行するか、手動セットアップに切り替えてください。`}
              </p>
              <div className="flex flex-wrap gap-2">
                {/* 失敗したcredential sourceを自動変更せず、利用者の明示選択へ戻す。 */}
                <Button type="button" size="sm" onClick={handleTerminalRecovery}>
                  {job?.reason === "invalid_credentials" &&
                  credentialMode !== "one_time_input"
                    ? "資格情報を選び直す"
                    : job?.reason === "invalid_credentials"
                      ? "ユーザー名・パスワードを再入力"
                      : "簡単セットアップを再実行"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onFallbackToManual}
                >
                  手動セットアップへ
                </Button>
              </div>
            </div>
          ) : null}

          {job && ["queued", "running"].includes(job.status) ? (
            <Button type="button" variant="outline" onClick={() => void cancel()}>処理を中止</Button>
          ) : null}
          {jobError || job?.status === "reconciliation_required" ? (
            <Button type="button" variant="outline" onClick={() => void refresh()}>状態を再確認</Button>
          ) : null}
          {job?.status === "cancel_requested" ? <p role="status">中止を受け付けました。処理の停止を確認しています。</p> : null}
          {job?.status === "canceled" ? <p role="status">処理を中止しました。</p> : null}
          {job?.status === "reconciliation_required" ? (
            <SetupInterruptionRecovery job={job} />
          ) : null}
          {job?.status === "succeeded" && !issuedToken ? (
            <p role="status">セットアップは完了しています。kintoneアプリ一覧から設定を確認できます。</p>
          ) : null}
          {jobError || probeError ? (
            <p className="text-xs text-destructive">{jobError ?? probeError}</p>
          ) : null}
        </div>
      )}

    </div>
  );
}
